<template>
  <div class="page-with-subnav">
    <app-sub-navigation-bar
      back-label="Back to all users"
      @back="goBack"
    />
    <div class="main-container page-with-subnav__content">
      <div class="ui grid user-form-grid">
        <div class="sixteen wide column">
          <h1 class="user-form-heading">
            <img src="../../assets/img/icn_avatar.png" alt="" class="user-form-heading-icon" width="32" height="32">
            {{ pageTitle }}
          </h1>
        </div>
        <div class="sixteen wide column">
          <app-form-error></app-form-error>
          <form class="ui form user-form-inner" @submit.prevent="submit">
            <div v-if="membership && membership.status === 'pending'" class="ui info message">
              Invitation pending — this user has not accepted yet.
            </div>

            <h4 class="ui dividing header">Personal Details</h4>
            <div class="two fields">
              <div class="field settings">
                <label>First Name*</label>
                <app-text-input type="text" placeholder="Enter first name" v-model="user.first_name" :disabled="isManagerMember" />
              </div>
              <div class="field settings">
                <label>Last Name*</label>
                <app-text-input type="text" placeholder="Enter last name" v-model="user.last_name" :disabled="isManagerMember" />
              </div>
            </div>
            <div class="field settings">
              <label>Email Address*</label>
              <app-text-input type="email" placeholder="Enter email address" v-model="user.email" :disabled="!isNew" />
            </div>

            <template v-if="canEditPermissions">
              <h4 class="ui dividing header">Permissions</h4>
              <div class="field settings">
                <app-field-label>Choose this user&rsquo;s level of access</app-field-label>
                <app-select
                  :items="roleOptions"
                  :value="user.type"
                  :disabled="isManagerMember"
                  @selected="onRoleSelected">
                </app-select>
                <a href="#" class="user-access-help-link" @click.prevent="showAccessHelp = true">Learn about user access</a>
              </div>
            </template>

            <div class="field user-form-actions">
              <button type="submit" class="ui primary button" v-if="!isManagerMember">{{ submitLabel }}</button>
              <button
                type="button"
                class="ui basic button"
                v-if="!isNew && membership && membership.status === 'pending'"
                @click="resendInvite"
              >Resend invitation</button>
              <button
                type="button"
                class="ui negative basic button"
                v-if="!isNew && membership && membership.status === 'pending'"
                @click="revokeInvite"
              >Revoke invitation</button>
              <button
                type="button"
                class="ui negative basic button"
                v-if="!isNew && membership && membership.status === 'active' && !isManagerMember"
                @click="removeMember"
              >Remove from account</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <app-standard-modal
      :open="showAccessHelp"
      title="User access"
      title-id="user-access-help-title"
      size="small"
      @close="showAccessHelp = false"
    >
      <p class="smardie-modal-help-intro">
        Choose the level of access that matches how this person will use Smardie.
      </p>
      <div
        v-for="item in accessHelpItems"
        :key="item.title"
        class="smardie-modal-help-item"
      >
        <h3>{{ item.title }}</h3>
        <p>{{ item.description }}</p>
      </div>
      <template #footer>
        <button type="button" class="ui primary button" @click="showAccessHelp = false">Close</button>
      </template>
    </app-standard-modal>
  </div>
</template>

<script>
import axios from 'axios'
import auth from '../../helpers/auth'
import SubNavigationBar from '../../components/common/SubNavigationBar.vue'

const emptyUser = () => ({
  first_name: '',
  last_name: '',
  email: '',
  type: 3
})

export default {
  name: 'UserForm',
  components: {
    'app-sub-navigation-bar': SubNavigationBar
  },
  data () {
    return {
      user: emptyUser(),
      membership: null,
      showAccessHelp: false,
      accessHelpItems: [
        {
          title: 'Owner',
          description: 'Full account access including users and billing.'
        },
        {
          title: 'Office',
          description: 'Manage quotes, invoices, customers, and catalog.'
        },
        {
          title: 'On-Site',
          description: 'Create and edit quotes in the field.'
        }
      ],
      roleOptions: [
        { id: 1, name: 'Owner' },
        { id: 2, name: 'Office' },
        { id: 3, name: 'On-Site' }
      ]
    }
  },
  computed: {
    isNew () {
      return this.$route.name === 'UserNew'
    },
    pageTitle () {
      if (this.isNew) return 'Invite User'
      const name = [this.user.first_name, this.user.last_name].filter(Boolean).join(' ')
      return name || 'Edit User'
    },
    submitLabel () {
      return this.isNew ? 'Invite' : 'Save Changes'
    },
    isManagerMember () {
      return !!(this.membership && this.membership.access_type === 'manager')
    },
    isOwnProfileEdit () {
      return String(this.$route.params.id) === 'me'
    },
    canEditPermissions () {
      if (this.isOwnProfileEdit) {
        return !this.$store.getters['user/isOnSiteStaff']
      }
      return true
    }
  },
  watch: {
    '$route' () {
      this.bootstrapFromRoute()
    }
  },
  created () {
    auth.check(this)
    this.bootstrapFromRoute()
  },
  methods: {
    goBack () {
      this.$router.push('/settings/account?tab=users')
    },
    setType (type) {
      if (this.isManagerMember) return
      this.user.type = type
    },
    onRoleSelected (payload) {
      if (this.isManagerMember) return
      const next = payload && payload.id != null ? Number(payload.id) : null
      if (next === 1 || next === 2 || next === 3) this.user.type = next
    },
    accessTypeToType (accessType) {
      if (accessType === 'owner' || accessType === 'manager') return 1
      if (accessType === 'office') return 2
      return 3
    },
    bootstrapFromRoute () {
      if (this.isNew) {
        this.user = emptyUser()
        this.membership = null
        return
      }
      const membershipId = this.$route.params.id
      if (!membershipId) return
      if (String(membershipId) === 'me') {
        axios.get('/api/auth/user').then((res) => {
          const u = res.data && res.data.user
          if (!u) {
            this.errorNotification('User not found')
            this.goBack()
            return
          }
          this.membership = {
            membership_id: u.id,
            access_type: u.access_type || null,
            status: 'active'
          }
          this.user = {
            membership_id: u.id,
            first_name: u.first_name || '',
            last_name: u.last_name || '',
            email: u.email || '',
            type: this.accessTypeToType(u.access_type || 'on_site')
          }
        }).catch(() => {
          this.errorNotification('Failed to load user')
          this.goBack()
        })
        return
      }
      axios.get('/api/invitations/company').then((res) => {
        const list = Array.isArray(res.data) ? res.data : []
        const m = list.find((row) => String(row.membership_id) === String(membershipId))
        if (!m) {
          this.errorNotification('User not found')
          this.goBack()
          return
        }
        this.membership = m
        this.user = {
          membership_id: m.membership_id,
          first_name: m.first_name || '',
          last_name: m.last_name || '',
          email: m.email || '',
          type: this.accessTypeToType(m.access_type)
        }
      }).catch(() => {
        this.errorNotification('Failed to load user')
        this.goBack()
      })
    },
    submit () {
      if (!this.user.first_name.trim() || !this.user.last_name.trim() || !this.user.email.trim()) {
        this.errorNotification('Please complete all required fields')
        return
      }
      if (this.canEditPermissions && !this.user.type) {
        this.errorNotification('Please choose a permission level')
        return
      }
      if (this.isNew) {
        this.invite()
        return
      }
      this.saveUser()
    },
    invite () {
      axios.post('/api/invitations', {
        first_name: this.user.first_name.trim(),
        last_name: this.user.last_name.trim(),
        email: this.user.email.trim(),
        type: this.user.type
      }).then(() => {
        this.successNotification('Invitation sent')
        this.goBack()
      }).catch((error) => {
        const msg = (error.response && error.response.data && error.response.data.error) || 'Failed to invite user'
        this.errorNotification(msg)
      })
    },
    saveUser () {
      if (!this.user.membership_id) return
      if (String(this.$route.params.id) === 'me') {
        const profile = this.$store.getters['user/getProfile'] || {}
        axios.put(`/api/users/${profile.id}`, {
          first_name: this.user.first_name.trim(),
          last_name: this.user.last_name.trim()
        }).then(() => {
          this.successNotification('User saved')
          this.$store.commit('user/setUserProfile', Object.assign({}, profile, {
            first_name: this.user.first_name.trim(),
            last_name: this.user.last_name.trim()
          }))
          this.goBack()
        }).catch((error) => {
          const msg = (error.response && error.response.data && error.response.data.error) || 'Failed to save user'
          this.errorNotification(msg)
        })
        return
      }
      axios.put(`/api/invitations/${this.user.membership_id}`, {
        first_name: this.user.first_name.trim(),
        last_name: this.user.last_name.trim(),
        type: this.user.type
      }).then(() => {
        this.successNotification('User saved')
        this.goBack()
      }).catch((error) => {
        const msg = (error.response && error.response.data && error.response.data.error) || 'Failed to save user'
        this.errorNotification(msg)
      })
    },
    resendInvite () {
      if (!this.user.membership_id) return
      axios.post(`/api/invitations/${this.user.membership_id}/resend`).then(() => {
        this.successNotification('Invitation resent')
      }).catch((error) => {
        const msg = (error.response && error.response.data && error.response.data.error) || 'Failed to resend invitation'
        this.errorNotification(msg)
      })
    },
    revokeInvite () {
      if (!this.user.membership_id) return
      axios.delete(`/api/invitations/${this.user.membership_id}`).then(() => {
        this.successNotification('Invitation revoked')
        this.goBack()
      }).catch((error) => {
        const msg = (error.response && error.response.data && error.response.data.error) || 'Failed to revoke invitation'
        this.errorNotification(msg)
      })
    },
    removeMember () {
      if (!this.user.membership_id) return
      axios.delete(`/api/invitations/member/${this.user.membership_id}`).then(() => {
        this.successNotification('User removed from account')
        this.goBack()
      }).catch((error) => {
        const msg = (error.response && error.response.data && error.response.data.error) || 'Failed to remove user'
        this.errorNotification(msg)
      })
    }
  }
}
</script>

<style scoped>
.page-with-subnav__content.main-container {
  margin-top: 16px;
}
.user-form-grid {
  max-width: 680px;
  margin-left: 0;
  margin-right: 0;
}
.user-form-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 0;
  color: #323C47;
}
.user-form-heading-icon {
  flex-shrink: 0;
}
.user-form-inner {
  width: 100%;
}
@media (min-width: 768px) {
  .user-form-inner {
    max-width: none;
  }
}
.user-permissions-copy {
  margin: 0 0 1rem;
  color: #323C47;
}
.user-access-help-link {
  display: inline-block;
  margin-top: 8px;
  color: var(--color-primary);
  font-size: 14px;
}
.user-form-actions {
  margin-top: 1rem;
}
.user-form-actions .button + .button {
  margin-left: 0.5rem;
}
</style>
